import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { X, Radar, TrendingUp, Loader2, ImageOff } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

const API = "/api";

function getAuthHeader() {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("http://") || url.startsWith("https://")) return url;
  return `/api${url.startsWith("/") ? url : "/" + url}`;
};

const formatHour = (h) => `${String(h).padStart(2, "0")}h`;

function ProductRow({ p }) {
  const photo = getImageUrl(p.photo_url);
  const probColor =
    p.probability >= 60 ? "text-emerald-600 dark:text-emerald-400" :
    p.probability >= 30 ? "text-amber-600 dark:text-amber-400" :
    "text-muted-foreground";
  // Frequência relativa: "1 a cada N pedidos" é mais legível que unidades absolutas
  // em horários de baixo volume (onde o cálculo de "esperado X un" arredondava pra 0).
  const rateN = p.probability >= 99.5
    ? 1
    : Math.max(2, Math.round(100 / Math.max(0.1, p.probability)));
  const rateLabel = rateN === 1 ? "em todo pedido" : `1 a cada ${rateN} pedidos`;
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-border/40 last:border-0">
      <div className="w-9 h-9 rounded-md bg-muted flex-shrink-0 overflow-hidden flex items-center justify-center">
        {photo ? (
          <img src={photo} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <ImageOff className="w-4 h-4 text-muted-foreground/50" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" title={p.name}>{p.name}</p>
        <p className="text-[10px] text-muted-foreground">
          ~{p.avg_qty.toString().replace(".", ",")} un por pedido · {rateLabel}
        </p>
      </div>
      <span className={`text-sm font-bold tabular-nums ${probColor}`}>{Math.round(p.probability)}%</span>
    </div>
  );
}

function HourColumn({ hourData, isCurrent }) {
  const { hour, expected_pedidos, real_pedidos, top_products } = hourData;
  return (
    <div className={`flex-1 min-w-0 bg-card border rounded-lg p-3 ${isCurrent ? "border-orange-500/60 shadow-md shadow-orange-500/10" : "border-border/60"}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <span className={`text-lg font-bold tabular-nums ${isCurrent ? "text-orange-500" : "text-foreground"}`}>
            {formatHour(hour)}
          </span>
          {isCurrent && (
            <span className="text-[9px] uppercase font-semibold text-orange-500 bg-orange-500/10 px-1.5 py-0.5 rounded">
              agora
            </span>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] text-muted-foreground">esp. / real</div>
          <div className="text-xs font-semibold tabular-nums">
            <span className="text-foreground">{Math.round(expected_pedidos)}</span>
            <span className="text-muted-foreground"> / </span>
            <span className={isCurrent ? "text-emerald-600 dark:text-emerald-400" : "text-foreground"}>{real_pedidos}</span>
          </div>
        </div>
      </div>
      {top_products && top_products.length > 0 ? (
        <div>
          {top_products.slice(0, 8).map(p => (
            <ProductRow key={p.product_id} p={p} />
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-xs text-muted-foreground">Sem histórico</div>
      )}
    </div>
  );
}

export default function RadarDetailsModal({ open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    axios.get(`${API}/delivery/radar/details`, getAuthHeader())
      .then(res => { if (mounted) setData(res.data); })
      .catch(err => { console.error("[Radar details] error:", err); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [open]);

  // Curva: combina hourly_today + hourly_expected num único array
  const chartData = useMemo(() => {
    if (!data) return [];
    const ex = new Map((data.hourly_expected || []).map(d => [d.hour, d.expected]));
    const tn = new Map((data.hourly_today || []).map(d => [d.hour, d.count]));
    const allHours = new Set([...ex.keys(), ...tn.keys()]);
    const hours = Array.from(allHours).sort((a, b) => a - b);
    // Filtra horas com alguma atividade (pra não poluir com 00h-05h sem nada)
    const filtered = hours.filter(h => (ex.get(h) || 0) > 0.1 || (tn.get(h) || 0) > 0);
    return filtered.map(h => ({
      hour: h,
      hourLabel: formatHour(h),
      esperado: ex.get(h) || 0,
      hoje: tn.get(h) || 0,
    }));
  }, [data]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-orange-500/10 text-orange-500 flex items-center justify-center flex-shrink-0">
              <Radar className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="font-bold text-base sm:text-lg leading-tight truncate">Radar de pedidos</h2>
                <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-violet-500/15 text-violet-500 border border-violet-500/30 flex-shrink-0">
                  Beta
                </span>
              </div>
              {data && (
                <p className="text-[11px] text-muted-foreground leading-tight">
                  {data.weekday_label} · dia {data.today_dom} ({data.today_bucket_label}) · todos os canais
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg hover:bg-muted flex items-center justify-center flex-shrink-0"
            title="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-5">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!loading && data && (
            <>
              {/* Fluxo: real hoje x esperado por hora */}
              <section>
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-muted-foreground" />
                  <h3 className="font-semibold text-sm">Fluxo de hoje vs esperado</h3>
                </div>
                <div className="h-56 sm:h-64 -mx-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgb(148 163 184 / 0.18)" />
                      <XAxis dataKey="hourLabel" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        contentStyle={{ background: "rgb(30 41 59 / 0.95)", border: "1px solid rgb(71 85 105 / 0.5)", borderRadius: 8, color: "white", fontSize: 12 }}
                        formatter={(v, name) => [Math.round(Number(v) * 10) / 10, name === "esperado" ? "Esperado" : "Hoje"]}
                      />
                      <ReferenceLine x={formatHour(data.now_hour)} stroke="#f97316" strokeDasharray="4 4" />
                      <Line
                        type="monotone"
                        dataKey="esperado"
                        stroke="#94a3b8"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={false}
                        name="esperado"
                      />
                      <Line
                        type="monotone"
                        dataKey="hoje"
                        stroke="#f97316"
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: "#f97316" }}
                        name="hoje"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center gap-4 text-[11px] text-muted-foreground mt-1 px-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-0.5 bg-orange-500" /> Hoje
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-0.5 bg-slate-400" style={{ borderTop: "2px dashed", borderColor: "#94a3b8" }} /> Esperado (média ponderada)
                  </div>
                </div>
              </section>

              {/* Top produtos por hora */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm">Probabilidade de saída por produto</h3>
                  <span className="text-[10px] text-muted-foreground">últimas {data.lookback_weeks} semanas</span>
                </div>
                <div className="flex flex-col lg:flex-row gap-3">
                  {data.per_hour && data.per_hour.map((hd, idx) => (
                    <HourColumn key={hd.hour} hourData={hd} isCurrent={idx === 0} />
                  ))}
                </div>
                <p className="text-[10px] text-muted-foreground mt-2 px-1">
                  % = chance do produto aparecer num pedido nesta (weekday, hora). Pesos: bucket do mês × recência.
                </p>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
